import { ThemeColors, ThemePresetName } from 'src/Model/WebCustomer';

/**
 * Built-in palettes for `TemplateData.Theme.Preset`.
 *
 * Each preset only lists the keys that differ from the stylesheet defaults
 * in src/_ui-tokens.scss. The page Ground is white on every light preset
 * (only espresso, being dark, sets its own); a brand that wants a tinted
 * page sets `Theme.Colors.Ground`. ThemeService fills the rest and derives
 * the secondary tokens (rings, tints, hairlines…) from the core colours, mixing
 * against the preset's own Card colour so tints stay correct on dark grounds.
 *
 * Contrast notes (WCAG, large/bold text on the accent):
 *   every AccentInk / Accent pair below is ≥ 4.5:1 except where marked.
 */
export type ThemePreset = Partial<ThemeColors>;

export const THEME_PRESETS: Record<Exclude<ThemePresetName, 'brand'>, ThemePreset> = {
  /* Raspberry — the reference look, identical to the _ui-tokens.scss defaults. */
  raspberry: {
    Accent: '#e11d62',
    AccentInk: '#ffffff',
    AccentHi: '#ff5f7c',
    AccentRing: '#f26e99',
    AccentTint: '#fff3f7',
    AccentBadge: '#ff4f84',
  },

  /* Chilli — crimson & warm neutrals; grills, steakhouses, pizzerias. */
  chilli: {
    Accent: '#b91c33',
    AccentInk: '#ffffff',
    AccentHi: '#dc3a52',
    Price: '#b91c33',
    BannerBg: '#160b0d',
  },

  /* Saffron — amber on cream, dark ink on the accent; South-Asian, Middle-Eastern, cafés. */
  saffron: {
    Accent: '#d97706',
    AccentInk: '#231607',
    AccentHi: '#f59e0b',
    Line: '#e6e1d6',
    Price: '#b45309',
    BannerBg: '#1c1409',
  },

  /* Paprika — burnt orange on warm off-white; Mediterranean, tapas, bakeries. */
  paprika: {
    Accent: '#c2410c',
    AccentInk: '#ffffff',
    AccentHi: '#ea580c',
    Line: '#e7dfd9',
    Price: '#c2410c',
    BannerBg: '#1a0f0a',
  },

  /* Mint — deep green; farm-to-table, vegetarian, coffee. Uses the shared
     success green like every light preset (it used to shift success to teal). */
  mint: {
    Accent: '#166534',
    AccentInk: '#ffffff',
    AccentHi: '#22865a',
    Line: '#dde3de',
    BannerBg: '#0b1a11',
  },

  /* Matcha — blue-green; seafood, sushi, juice bars, tea rooms. Sits between
     mint (deeper, yellower) and blueberry. */
  matcha: {
    Accent: '#0f766e',
    AccentInk: '#ffffff',
    AccentHi: '#14968b',
    Line: '#d9e5e2',
    BannerBg: '#07191a',
  },

  /* Blueberry — navy; hotels, fine dining, formal brands. */
  blueberry: {
    Accent: '#1e3a8a',
    AccentInk: '#ffffff',
    AccentHi: '#3556c7',
    Line: '#dfe2ec',
    BannerBg: '#0b1030',
  },

  /* Aubergine — plum; dessert, cocktail bars, patisseries. */
  aubergine: {
    Accent: '#6b21a8',
    AccentInk: '#ffffff',
    AccentHi: '#8b3fd9',
    Line: '#e5dfec',
    BannerBg: '#150a22',
  },

  /* Lemon — yellow accent with dark ink on it; juice bars, breakfast, summer
     menus. Like saffron the accent itself is light (≈2.2:1 on white), so
     rings and badges read softer than on the other presets. */
  lemon: {
    Accent: '#d9ad00',
    AccentInk: '#1f1a05',
    AccentHi: '#f0c419',
    Line: '#ebe6cf',
    BannerBg: '#1a1607',
  },

  /* Tamarind — deep pulp brown with warm neutrals; Indian, Thai, Mexican, cafés. */
  tamarind: {
    Accent: '#6b3a1e',
    AccentInk: '#ffffff',
    AccentHi: '#8f5330',
    Line: '#e8ddd2',
    Price: '#9a3412',
    BannerBg: '#1a0f08',
  },

  /* Beetroot — maroon; wine bars, bistros, heritage brands. */
  beetroot: {
    Accent: '#7a1a2b',
    AccentInk: '#ffffff',
    AccentHi: '#a02a41',
    Line: '#eadfe2',
    Price: '#7a1a2b',
    BannerBg: '#170709',
  },

  /* Charcoal — monochrome, red only for prices and errors; minimal, premium. */
  charcoal: {
    Accent: '#1f2937',
    AccentInk: '#ffffff',
    AccentHi: '#374151',
    Line: '#e2e4e8',
    Price: '#b91c1c',
    BannerBg: '#0b0d12',
  },

  /* Espresso — dark surfaces with a gold (crema) accent. Best-effort: photo overlays and the
     Material datepicker are token-driven, but legacy screens (old header,
     old menu, login) are not and will stay light. */
  espresso: {
    Ground: '#0f1115',
    Card: '#171a21',
    Line: '#262b35',
    Ink: '#f3f4f6',
    Accent: '#f0a836',
    AccentInk: '#14120c',
    AccentHi: '#f7c15c',
    Success: '#34d399',
    SuccessBadge: '#10b981',
    Danger: '#fb7185',
    Price: '#f3f4f6', /* = Ink: rose is reserved for danger / favourites on dark */
    HeaderBg: '#12141a',
    HeaderInk: '#f3f4f6',
    BannerBg: '#05060a',
    BannerText: '#ffffff',
    ThumbBg: '#23272f',
  },
};

/**
 * Used when a brand sets `Theme` but names no `Preset`. 'brand' lifts the
 * accent from the colours that brand already configured under
 * DynamicStyle.SecondaryColors, so the themed surfaces match the rest of the
 * site instead of everyone defaulting to the same pink.
 */
export const DEFAULT_THEME_PRESET: ThemePresetName = 'brand';

/**
 * The palette to fall back on: when `Preset` names something unknown, and
 * when 'brand' is in play but the brand has no colours to lift. Must be a
 * real palette, which is why it cannot just be DEFAULT_THEME_PRESET.
 */
export const FALLBACK_THEME_PRESET: Exclude<ThemePresetName, 'brand'> =
  'raspberry';
